/**
 * Simple feature flag system for Statura Care.
 *
 * Flags are read from environment variables prefixed with FF_.
 * Example: FF_MEDICATION_ALERTS=true enables the 'medication-alerts' flag.
 *
 * In K8s, flags are set via the ConfigMap. For local dev, use .env.
 * This intentionally avoids external dependencies (LaunchDarkly, etc.)
 * since the team is small and deploy-time flags are sufficient.
 */

export interface FeatureFlags {
  /** Global fail-off for the governed site-wide experience candidate. */
  'experience-v1': boolean;
  /** Enable async notification processing via BullMQ queues */
  'async-notifications': boolean;
  /** Enable medication interaction alerts from clinical service */
  'medication-alerts': boolean;
  /** Enable S8 controlled substance discrepancy alerts */
  's8-alerts': boolean;
  /** Enable care plan review reminder notifications */
  'care-plan-reminders': boolean;
  /** Enable screening expiry warning notifications */
  'screening-expiry-warnings': boolean;
  /** Enable OpenTelemetry distributed tracing */
  'otel-tracing': boolean;
  /** Enable family portal real-time updates via WebSocket */
  'family-realtime': boolean;
  /** Enable AI-powered care plan suggestions */
  'ai-care-suggestions': boolean;
}

const FLAG_PREFIX = 'FF_';

/**
 * Convert flag name to env var name.
 * 'medication-alerts' → 'FF_MEDICATION_ALERTS'
 */
function flagToEnvVar(flag: string): string {
  return `${FLAG_PREFIX}${flag.toUpperCase().replace(/-/g, '_')}`;
}

/**
 * Check if a feature flag is enabled.
 * Returns false if the flag is not set.
 */
export function isFeatureEnabled(flag: keyof FeatureFlags): boolean {
  const envVar = flagToEnvVar(flag);
  const value = process.env[envVar];
  if (value === undefined || value === '') return false;
  return value === 'true' || value === '1' || value === 'yes';
}

/**
 * Get all feature flags and their current state.
 * Useful for health endpoints and debugging.
 */
export function getAllFlags(): Record<keyof FeatureFlags, boolean> {
  const flags: (keyof FeatureFlags)[] = [
    'experience-v1',
    'async-notifications',
    'medication-alerts',
    's8-alerts',
    'care-plan-reminders',
    'screening-expiry-warnings',
    'otel-tracing',
    'family-realtime',
    'ai-care-suggestions',
  ];
  return Object.fromEntries(flags.map((f) => [f, isFeatureEnabled(f)])) as Record<
    keyof FeatureFlags,
    boolean
  >;
}

/**
 * Require a feature flag to be enabled, or throw.
 * Use in module initialization to gate entire modules.
 */
export function requireFeature(flag: keyof FeatureFlags, context?: string): void {
  if (!isFeatureEnabled(flag)) {
    throw new Error(
      `Feature '${flag}' is not enabled${context ? ` (required by ${context})` : ''}. ` +
        `Set ${flagToEnvVar(flag)}=true to enable.`,
    );
  }
}
